import {ActivityHeader} from '@github-ui/commenting/ActivityHeader'
import {ReactionViewerBase} from '@github-ui/reaction-viewer/ReactionViewerBase'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import type {IssueComment} from '../../page-data/payloads/issue-comments'
import type {ReactionContent} from '@github-ui/reaction-viewer/ReactionGroupsUtils'
import {useReactToIssueCommentMutation} from '../../mutations/use-react-to-issue-comment-mutation'
import {type SelectionContext, selectQuoteFromComment} from '@github-ui/commenting/quotes'
import {use, useRef} from 'react'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {useLayoutCanComment} from '../../stores/layout-selectors'
import {CommentLayout} from './CommentLayout'
import {ConversationItemActions, ConversationItemActionsProvider} from './ConversationItemActions'
import {CommentsContext} from './CommentsContext'
import {issueCommentAnchor} from './utils/comment-anchors'
import {useFocusHashTarget} from '../../hooks/use-focus-hash-target'

interface IssueCommentItemProps {
  comment: IssueComment
  pullRequestAuthorLogin: string
  canReact: boolean
  // Optional callback for handling quote reply actions. If provided, a "Quote reply" button will be rendered.
  onQuoteReply?: (quotedText: string) => void
  className?: string
}

export function IssueCommentItem({
  comment,
  pullRequestAuthorLogin,
  canReact,
  onQuoteReply: externalOnQuoteReply,
  className,
}: IssueCommentItemProps): React.ReactElement | null {
  const {mutate: reactToCommentMutation} = useReactToIssueCommentMutation()
  const currentUserCanComment = useLayoutCanComment()

  const commentRef = useRef<HTMLElement>(null)

  const anchorPrefix = use(CommentsContext).commentAnchorPrefix
  const id = issueCommentAnchor(comment.id, anchorPrefix)
  useFocusHashTarget(id)

  if (!comment.repository) return null

  const onReact = (reaction: ReactionContent, viewerHasReacted: boolean) => {
    reactToCommentMutation({
      commentId: comment.id,
      reaction,
      viewerHasReacted,
    })
  }

  const onQuoteReply =
    currentUserCanComment && externalOnQuoteReply
      ? () => {
          const current = ssrSafeWindow?.getSelection()
          let currentSelection: SelectionContext | null = null

          if (current && current.anchorNode && current.rangeCount > 0) {
            currentSelection = {
              anchorNode: current.anchorNode,
              range: current.getRangeAt(0),
            }
          }

          const quotedText = selectQuoteFromComment(commentRef.current, currentSelection, comment.body)
          externalOnQuoteReply(quotedText ?? comment.body)
        }
      : undefined

  return (
    <ConversationItemActionsProvider onQuoteReply={onQuoteReply}>
      <CommentLayout
        id={id}
        ref={commentRef}
        className={className}
        tabIndex={-1}
        header={
          <ActivityHeader
            avatarUrl={comment.authorAvatarUrl || ''}
            comment={{
              ...comment,
              repository: comment.repository,
            }}
            commentAuthorLogin={comment.authorLogin}
            commentSubjectType="pull request"
            commentSubjectAuthorLogin={pullRequestAuthorLogin}
            forceInlineAvatar
            isMinimized={false}
            noContainerStyles
            actions={<ConversationItemActions onQuoteReply={onQuoteReply} link={comment.url} />}
          />
        }
      >
        <VerifiedHTMLDiv className="markdown-body mt-1 f5" html={comment.bodyHtml} />
        {comment.reactionGroups.length > 0 && (
          <div className="mt-2">
            <ReactionViewerBase
              reactionGroups={comment.reactionGroups}
              canReact={canReact && comment.viewerCanReact}
              onReact={onReact}
            />
          </div>
        )}
      </CommentLayout>
    </ConversationItemActionsProvider>
  )
}
